import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, ActivationStart, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { UserQuery } from '@core/store/user/user.query';
import { UserModel } from '@core/store/user/user.model';
import { HeaderComponent } from './components/header/header.component';
import { ToastContainerComponent } from './components/toast-container/toast-container.component';
import { AsyncPipe } from '@angular/common';
import { APP_ORIGINS, absoluteUrl } from '@core/config/app-surface';

@Component({
  selector: 'app-core',
  templateUrl: './core.component.html',
  styleUrls: ['./core.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [HeaderComponent, ToastContainerComponent, RouterLink, RouterOutlet, AsyncPipe],
  // A v2 route owns its own chrome. The bootstrap root marks itself so the
  // stylesheet can stand the old ground down underneath it.
  host: { '[class.v2-route]': 'isV2' }
})
export class CoreComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly userQuery = inject(UserQuery);

  isCollapsed = true;
  breadcrumbs = '';
  hideHeader = false;

  /**
   * Set from the deepest activated route's `data`. A route carrying
   * `{ v2: true }` renders a bare outlet: the v2 shell brings its own header,
   * navigation, toasts and ground, and would otherwise be wrapped in the old
   * chrome because this component is the bootstrap root rather than a routed one.
   */
  isV2 = false;

  /**
   * The briefings surface, which is a host of its own now. The old sidebar's
   * "Airport Briefings" entry is the one link in this chrome that leaves the
   * estimator origin.
   */
  readonly briefingsHref = absoluteUrl('briefings', '/', APP_ORIGINS);

  user = this.userQuery.getUser$;
  isAdmin$ = this.user.pipe(map((user: UserModel | null) => !!user?.is_staff));

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}


  ngOnInit() {
    this.router.events.pipe(
      filter((e) => e instanceof ActivationStart)
    ).subscribe(( ) => {
      this.isCollapsed = true;
    });

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe(({breadcrumbs, hideHeader, v2}) => {
        this.hideHeader = hideHeader;
        this.breadcrumbs = breadcrumbs;
        this.isV2 = !!v2;
      });
  }

  toggleMenu() {
    this.isCollapsed = !this.isCollapsed;
  }
}
